import type {
  PhongMaterial,
  StandardMaterial,
  PhysicalMaterial,
  BasicMaterial,
  LambertMaterial,
  Material,
} from "../types/material";
import type { Vector4, MaterialUUID, TextureUUID } from "../types/primitives";
import { generateUuid } from "./id";

const DEFAULT_COLOR: Vector4 = [0.8, 0.8, 0.8, 1];
const DEFAULT_SPECULAR: Vector4 = [0.2, 0.2, 0.2, 1];

const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(Math.max(value, min), max) : min;

const normalizeVector4 = (value?: Vector4 | number[]): Vector4 => {
  const source = Array.isArray(value) ? value : DEFAULT_COLOR;
  const normalized: Vector4 = [
    DEFAULT_COLOR[0],
    DEFAULT_COLOR[1],
    DEFAULT_COLOR[2],
    DEFAULT_COLOR[3],
  ];
  source.slice(0, 4).forEach((component, index) => {
    if (typeof component === "number" && Number.isFinite(component)) {
      normalized[index] = index === 3 ? clamp(component, 0, 1) : clamp(component, 0, 1);
    }
  });
  if (source.length < 4 && source[3] !== undefined && typeof source[3] === "number") {
    normalized[3] = clamp(source[3], 0, 1);
  }
  return normalized;
};

type GeneratedDefaultMaterial = PhongMaterial & { autoGenerated: true };

export function createDefaultPhongMaterial(
  name: string,
  overrides: Partial<PhongMaterial> = {}
): PhongMaterial {
  const color = normalizeVector4(overrides.color);
  const specular = normalizeVector4(overrides.specular ?? DEFAULT_SPECULAR);
  const opacity = typeof overrides.opacity === "number" ? clamp(overrides.opacity, 0, 1) : color[3];
  const transparent =
    typeof overrides.transparent === "boolean" ? overrides.transparent : opacity < 1;
  const alphaMode = overrides.alphaMode ?? (transparent ? "blend" : "opaque");
  const shininess =
    typeof overrides.shininess === "number" && Number.isFinite(overrides.shininess)
      ? overrides.shininess
      : 32;

  const material: PhongMaterial = {
    uuid: overrides.uuid ?? (generateUuid() as unknown as MaterialUUID),
    kind: "phong",
    name,
    color,
    specular,
    shininess,
    opacity,
    transparent,
    alphaMode,
    alphaCutoff: overrides.alphaCutoff ?? 0.5,
    doubleSided: overrides.doubleSided ?? true,
    ...overrides,
  };

  material.kind = "phong";
  material.name = name;
  material.color = color;
  material.specular = specular;
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaMode = alphaMode;
  material.shininess = shininess;

  if (typeof overrides.colorMap === "string") {
    material.colorMap = overrides.colorMap as TextureUUID;
  }

  if (material.opacity == null) {
    material.opacity = color[3];
  }
  if (material.transparent == null) {
    material.transparent = (material.opacity ?? 1) < 1;
  }

  return material;
}

export function createDefaultStandardMaterial(
  name: string,
  overrides: Partial<StandardMaterial> = {}
): StandardMaterial {
  const color = normalizeVector4(overrides.color);
  const opacity = typeof overrides.opacity === "number" ? clamp(overrides.opacity, 0, 1) : color[3];
  const transparent =
    typeof overrides.transparent === "boolean" ? overrides.transparent : opacity < 1;
  const alphaMode = overrides.alphaMode ?? (transparent ? "blend" : "opaque");

  const material: StandardMaterial = {
    uuid: overrides.uuid ?? (generateUuid() as unknown as MaterialUUID),
    kind: "standard",
    name,
    color,
    metalness: 0,
    roughness: 0.5,
    opacity,
    transparent,
    alphaMode,
    alphaCutoff: overrides.alphaCutoff ?? 0.5,
    doubleSided: overrides.doubleSided ?? true,
    ...overrides,
  };

  material.kind = "standard";
  material.name = name;
  material.color = color;
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaMode = alphaMode;

  if (typeof overrides.metalness === "number") {
    material.metalness = clamp(overrides.metalness, 0, 1);
  }
  if (typeof overrides.roughness === "number") {
    material.roughness = clamp(overrides.roughness, 0, 1);
  }

  if (typeof overrides.colorMap === "string") {
    material.colorMap = overrides.colorMap as TextureUUID;
  }

  return material;
}

export function createDefaultPhysicalMaterial(
  name: string,
  overrides: Partial<PhysicalMaterial> = {}
): PhysicalMaterial {
  const color = normalizeVector4(overrides.color);
  const opacity = typeof overrides.opacity === "number" ? clamp(overrides.opacity, 0, 1) : color[3];
  const transparent =
    typeof overrides.transparent === "boolean" ? overrides.transparent : opacity < 1;
  const alphaMode = overrides.alphaMode ?? (transparent ? "blend" : "opaque");

  const material: PhysicalMaterial = {
    uuid: overrides.uuid ?? (generateUuid() as unknown as MaterialUUID),
    kind: "physical",
    name,
    color,
    metalness: 0,
    roughness: 0.5,
    opacity,
    transparent,
    alphaMode,
    alphaCutoff: overrides.alphaCutoff ?? 0.5,
    doubleSided: overrides.doubleSided ?? true,
    clearcoat: 0,
    clearcoatRoughness: 0,
    transmission: 0,
    thickness: 0,
    ior: 1.5,
    ...overrides,
  };

  material.kind = "physical";
  material.name = name;
  material.color = color;
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaMode = alphaMode;

  if (typeof overrides.metalness === "number") {
    material.metalness = clamp(overrides.metalness, 0, 1);
  }
  if (typeof overrides.roughness === "number") {
    material.roughness = clamp(overrides.roughness, 0, 1);
  }
  if (typeof overrides.clearcoat === "number") {
    material.clearcoat = clamp(overrides.clearcoat, 0, 1);
  }
  if (typeof overrides.clearcoatRoughness === "number") {
    material.clearcoatRoughness = clamp(overrides.clearcoatRoughness, 0, 1);
  }
  if (typeof overrides.transmission === "number") {
    material.transmission = clamp(overrides.transmission, 0, 1);
  }
  if (typeof overrides.thickness === "number") {
    material.thickness = Math.max(0, overrides.thickness);
  }
  if (typeof overrides.ior === "number") {
    material.ior = Math.max(1, overrides.ior);
  }

  if (typeof overrides.colorMap === "string") {
    material.colorMap = overrides.colorMap as TextureUUID;
  }

  return material;
}

export function createDefaultBasicMaterial(
  name: string,
  overrides: Partial<BasicMaterial> = {}
): BasicMaterial {
  const color = normalizeVector4(overrides.color);
  const opacity = typeof overrides.opacity === "number" ? clamp(overrides.opacity, 0, 1) : color[3];
  const transparent =
    typeof overrides.transparent === "boolean" ? overrides.transparent : opacity < 1;
  const alphaMode = overrides.alphaMode ?? (transparent ? "blend" : "opaque");

  const material: BasicMaterial = {
    uuid: overrides.uuid ?? (generateUuid() as unknown as MaterialUUID),
    kind: "basic",
    name,
    color,
    opacity,
    transparent,
    alphaMode,
    alphaCutoff: overrides.alphaCutoff ?? 0.5,
    doubleSided: overrides.doubleSided ?? true,
    ...overrides,
  };

  material.kind = "basic";
  material.name = name;
  material.color = color;
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaMode = alphaMode;

  if (typeof overrides.colorMap === "string") {
    material.colorMap = overrides.colorMap as TextureUUID;
  }

  return material;
}

export function createDefaultLambertMaterial(
  name: string,
  overrides: Partial<LambertMaterial> = {}
): LambertMaterial {
  const color = normalizeVector4(overrides.color);
  const opacity = typeof overrides.opacity === "number" ? clamp(overrides.opacity, 0, 1) : color[3];
  const transparent =
    typeof overrides.transparent === "boolean" ? overrides.transparent : opacity < 1;
  const alphaMode = overrides.alphaMode ?? (transparent ? "blend" : "opaque");

  const material: LambertMaterial = {
    uuid: overrides.uuid ?? (generateUuid() as unknown as MaterialUUID),
    kind: "lambert",
    name,
    color,
    opacity,
    transparent,
    alphaMode,
    alphaCutoff: overrides.alphaCutoff ?? 0.5,
    doubleSided: overrides.doubleSided ?? true,
    ...overrides,
  };

  material.kind = "lambert";
  material.name = name;
  material.color = color;
  material.opacity = opacity;
  material.transparent = transparent;
  material.alphaMode = alphaMode;

  if (typeof overrides.colorMap === "string") {
    material.colorMap = overrides.colorMap as TextureUUID;
  }

  return material;
}

export function normalizeMaterialColor(color?: Vector4 | number[]): Vector4 {
  return normalizeVector4(color);
}

/** Ensures a `default_mat` entry exists in the scene materials record and returns its UUID. */
export function ensureDefaultMatMaterialEntry(materials: Record<string, Material>): MaterialUUID {
  const existing = Object.entries(materials).find(([, mat]) => mat?.name === "default_mat");
  if (existing) return existing[0] as MaterialUUID;

  const generated: GeneratedDefaultMaterial = {
    ...createDefaultPhongMaterial("default_mat", {
      color: [0.75, 0.75, 0.75, 1],
    }),
    autoGenerated: true,
  };
  materials[generated.uuid] = generated;
  return generated.uuid as MaterialUUID;
}
